import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Notification from "../components/Notification";
import { API_URL } from "../config";
import '../styles/SignIn.css';
import { useRecaptcha, ROBOT_MESSAGE } from "../components/Recaptcha";

// The emails a member can stop or restart here. The link at the foot of an
// email opens this page with the right one chosen (?list=events).
const LISTS = {
    profile: {
        option: "Profile reminders",
        name: "profile reminders",
        about: "reminders to renew or complete your profile",
    },
    events: {
        option: "Emails about new events",
        name: "emails about new events",
        about: "emails sent when a new event is added",
    },
};

function ProfileReminders() {
    const [searchParams] = useSearchParams();
    const [list, setList] = useState(searchParams.get("list") === "events" ? "events" : "profile");
    const chosen = LISTS[list];
    const [email, setEmail] = useState("");
    const [action, setAction] = useState("unsubscribe"); // unsubscribe or resubscribe
    const [notification, setNotification] = useState({ type: "", message: "", isVisible: false });
    const [errors, setErrors] = useState({
        email: ""
    });
    const [loading, setLoading] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(null);

    const validate = () => {
        let isValid = true;
        const errors = {};

        if (!email) {
            errors.email = "Email is required.";
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            errors.email = "Email is invalid.";
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    const robot = useRecaptcha();

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        
        if (validate()) {
            if (robot.missing) {
                setNotification({ type: "danger", message: ROBOT_MESSAGE, isVisible: true });
                setLoading(false);
                return;
            }

            try {
                const endpoint = action === "unsubscribe" 
                    ? "/profile/reminders/unsubscribe"
                    : "/profile/reminders/resubscribe";

                const response = await fetch(API_URL + endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        email: email,
                        list: list,
                        recaptcha_token: robot.token
                    }),
                });

                const data = await response.json();
                robot.reset(); // An answer can be used only once.

                if (response.ok) {
                    const successMessage = action === "unsubscribe" 
                        ? `You have been unsubscribed from ${chosen.name}.`
                        : `You have been resubscribed to ${chosen.name}.`;
                    
                    setNotification({ type: "success", message: successMessage, isVisible: true });
                    setCurrentStatus(data.data?.receive_reminders);
                    setEmail(""); // Clear email after successful action
                } else {
                    setNotification({ type: "danger", message: data.message || "Request failed. Please try again.", isVisible: true });
                    if (data.email) {
                        setErrors((prevErrors) => ({
                            ...prevErrors,
                            email: data.email.join(", "),
                        }));
                    }
                }
            } catch (error) {
                console.log(error);
                setNotification({ type: "danger", message: "An error occurred. Please try again later.", isVisible: true });
            } finally {
                setLoading(false);
            }
        } else {
            setNotification({ type: "danger", message: "Please fix the errors in the form.", isVisible: true });
            setLoading(false);
        }
    };

    const checkStatus = async () => {
        if (!email) {
            setErrors({ email: "Email is required to check status." });
            return;
        }

        if (!validate()) return;

        if (robot.missing) {
            setNotification({ type: "danger", message: ROBOT_MESSAGE, isVisible: true });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(API_URL + "/profile/reminders/check-status", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: email,
                    list: list,
                    recaptcha_token: robot.token
                }),
            });

            const data = await response.json();
            robot.reset(); // An answer can be used only once.

            if (response.ok) {
                const statusText = data.data.receive_reminders 
                    ? `Currently receiving ${chosen.name}` 
                    : `Not receiving ${chosen.name}`;
                const profileStatus = list === "profile" && data.data.status ? ` (Profile status: ${data.data.status})` : '';
                
                setNotification({ 
                    type: "info", 
                    message: `${statusText}${profileStatus}`, 
                    isVisible: true 
                });
                setCurrentStatus(data.data.receive_reminders);
            } else {
                setNotification({ type: "danger", message: data.message || "Unable to check status.", isVisible: true });
            }
        } catch (error) {
            console.log(error);
            setNotification({ type: "danger", message: "An error occurred. Please try again later.", isVisible: true });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, isVisible: false });
    };

    return (
        <div className="cf-public">
            <Header />
            <main>
                <div className="signin-page">
                    <div className="signin-card">
                        <div className="signin-content">
                            <div className="content">
                                <h1 className="sign-in-title">Email preferences</h1>

                                <p className="cf-muted mb-4">Choose which emails you mean, then enter your email address to unsubscribe or resubscribe.</p>

                                {notification.isVisible && (
                                    <Notification
                                        type={notification.type}
                                        message={notification.message}
                                        onClose={handleCloseNotification}
                                    />
                                )}

                                <form onSubmit={handleSubmit}>
                                    <div className="field">
                                        <label className="label" htmlFor="email-list">Which emails?</label>
                                        <div className="control">
                                            <div className="select is-fullwidth">
                                                <select
                                                    id="email-list"
                                                    value={list}
                                                    onChange={(e) => { setList(e.target.value); setCurrentStatus(null); }}
                                                >
                                                    {Object.entries(LISTS).map(([key, item]) => (
                                                        <option key={key} value={key}>{item.option}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`field ${errors.email ? 'is-danger' : ''}`}>
                                        <label className="label">Email Address</label>
                                        <div className="control has-icons-left has-icons-right">
                                            <input
                                                className={`input ${errors.email ? 'is-danger' : ''}`}
                                                type="email"
                                                placeholder="Enter your email address"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                            />
                                            <span className="icon is-small is-left">
                                                <i className="fas fa-envelope"></i>
                                            </span>
                                            {errors.email && (
                                                <span className="icon is-small is-right">
                                                    <i className="fas fa-exclamation-triangle"></i>
                                                </span>
                                            )}
                                        </div>
                                        {errors.email && <p className="help is-danger">{errors.email}</p>}
                                    </div>

                                    {robot.element}

                                    <div className="field">
                                        <div className="control">
                                            <button 
                                                type="button" 
                                                className="button is-fullwidth primary-outline-button mb-2"
                                                onClick={checkStatus}
                                                disabled={loading || !email}
                                            >
                                                {loading ? "Checking..." : "Check Current Status"}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="field is-grouped" style={{ gap: '10px' }}>
                                        <div className="control is-expanded">
                                            <button 
                                                type="submit" 
                                                className="button is-fullwidth danger-outline-button"
                                                onClick={() => setAction("unsubscribe")}
                                                disabled={loading || !email}
                                            >
                                                {loading && action === "unsubscribe" ? "Processing..." : "Unsubscribe"}
                                            </button>
                                        </div>
                                        <div className="control is-expanded">
                                            <button 
                                                type="submit" 
                                                className="button is-fullwidth primary-bg"
                                                onClick={() => setAction("resubscribe")}
                                                disabled={loading || !email}
                                            >
                                                {loading && action === "resubscribe" ? "Processing..." : "Resubscribe"}
                                            </button>
                                        </div>
                                    </div>
                                </form>

                                <div className="content mt-5">
                                    <div className="cf-callout">
                                        <h4>What does this do?</h4>
                                        <ul>
                                            <li><strong>Unsubscribe:</strong> Stop receiving {chosen.about}</li>
                                            <li><strong>Resubscribe:</strong> Start receiving them again</li>
                                            <li><strong>Check Status:</strong> See your current choice</li>
                                        </ul>
                                        <p className="mt-3 is-size-7">
                                            Note: This only affects {chosen.name}. You may still receive other important emails from the portal{list === "events" ? ", and new events still appear in your notifications when you sign in" : ""}.
                                        </p>
                                    </div>
                                </div>

                                <p className="cf-auth-links">
                                    <Link to="/">Back to home</Link>
                                </p>

                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default ProfileReminders;